import { GridActionsCellItem } from '@mui/x-data-grid';
import {
  Paper,
  IconButton,
  Grid,
  Tooltip,
  Button,
  Tab,
  Tabs,
  Badge,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Visibility,
  PauseCircle,
  Settings as SettingsIcon,
  PlayCircle,
  Dashboard as DashboardIcon,
  PlayArrow as ContinueIcon,
} from '@mui/icons-material';
import { GridRowParams } from '@mui/x-data-grid';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link as RouterLink, useNavigate } from 'react-router';
import { useSnackbar } from 'notistack';
import useConfirmDeleteDialog from '../../hooks/useConfirmDeleteDialog';
import useCheckPermission from '../../hooks/useCheckPermission';
import tenantService from './tenantService';
import tenantOnboardingService from './tenantOnboardingService';
import DataList from '../../components/DataList';
import { TenantStatus, TenantTypes } from './TenantForm';

const DRAFT_STATUS_IN_PROGRESS = 0;

const TenantsPage = () => {
  const navigate = useNavigate();
  const { ConfirmDeleteDialog, setParams } = useConfirmDeleteDialog();
  const [rows, setRows] = useState<any[]>([]);
  const [drafts, setDrafts] = useState<any[]>([]);
  const [tab, setTab] = useState(0);
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch<any>();
  const { canPlatform } = useCheckPermission();

  const loadLists = () => {
    dispatch({ type: 'SHOW_QUERY' });
    Promise.all([tenantService.list(), tenantOnboardingService.list()])
      .then(([tenantResponse, draftResponse]) => {
        if (!tenantResponse.hasError) setRows(tenantResponse.records);
        else
          enqueueSnackbar(tenantResponse.errorMessages.join(','), {
            variant: 'error',
          });
        if (!draftResponse.hasError) {
          setDrafts(
            (draftResponse.records ?? []).filter(
              (d: any) => (d.status ?? d.Status) === DRAFT_STATUS_IN_PROGRESS
            )
          );
        }
      })
      .finally(() => dispatch({ type: 'HIDE' }));
  };

  useEffect(() => {
    loadLists();
  }, []);

  const getActions = (params: GridRowParams) => {
    const actions: any[] = [];
    const readonly = !canPlatform(['ManageTenants']);
    const isSuspended = params.row.status === TenantStatus.Suspended;

    actions.push(
      <GridActionsCellItem
        key="overview"
        showInMenu
        icon={<DashboardIcon />}
        label="Bérlő 360"
        onClick={() => navigate(`/tenants/${params.row.id}/overview`)}
      />
    );

    actions.push(
      <GridActionsCellItem
        key="edit"
        showInMenu
        icon={readonly ? <Visibility /> : <EditIcon />}
        label={readonly ? 'Megtekintés' : 'Szerkesztés'}
        onClick={() => navigate(`/tenants/edit/${params.row.id}`)}
      />
    );

    if (canPlatform(['ManageTenants'])) {
      actions.push(
        <GridActionsCellItem
          key="settings"
          showInMenu
          icon={<SettingsIcon />}
          label="Beállítások"
          onClick={() => navigate(`/tenants/${params.row.id}/admin`)}
        />
      );
      if (isSuspended) {
        actions.push(
          <GridActionsCellItem
            key="unsuspend"
            showInMenu
            icon={<PlayCircle />}
            label="Felfüggesztés feloldása"
            onClick={() => {
              tenantService.unsuspend(params.row.id).then((response) => {
                if (response.hasError) {
                  enqueueSnackbar((response.errorMessages ?? []).join(','), {
                    variant: 'error',
                  });
                } else {
                  enqueueSnackbar('Bérlő aktiválva', { variant: 'success' });
                  setRows((prev) =>
                    prev.map((row) =>
                      row.id === params.row.id
                        ? { ...row, status: TenantStatus.Active }
                        : row
                    )
                  );
                }
              });
            }}
          />
        );
      } else {
        actions.push(
          <GridActionsCellItem
            key="suspend"
            showInMenu
            icon={<PauseCircle />}
            label="Felfüggesztés"
            onClick={() => {
              tenantService.suspend(params.row.id).then((response) => {
                if (response.hasError) {
                  enqueueSnackbar((response.errorMessages ?? []).join(','), {
                    variant: 'error',
                  });
                } else {
                  enqueueSnackbar('Bérlő felfüggesztve', { variant: 'success' });
                  setRows((prev) =>
                    prev.map((row) =>
                      row.id === params.row.id
                        ? { ...row, status: TenantStatus.Suspended }
                        : row
                    )
                  );
                }
              });
            }}
          />
        );
      }
      actions.push(
        <GridActionsCellItem
          key="delete"
          showInMenu
          icon={<DeleteIcon />}
          label="Törlés"
          onClick={() =>
            setParams({
              open: true,
              name: 'Biztosan törölni szeretnéd ezt a bérlőt?',
              onConfirm: async () => handleDelete(params.row.id),
            })
          }
        />
      );
    }

    return actions;
  };

  const columns: any[] = [
    { field: 'name', headerName: 'Név', flex: 120 },
    { field: 'slug', headerName: 'Slug', flex: 80 },
    {
      field: 'type',
      headerName: 'Típus',
      flex: 80,
      valueFormatter: (value: any) =>
        value === TenantTypes.Individual
          ? 'Magánszemély'
          : value === TenantTypes.Government
            ? 'Kormányzati'
            : 'Szervezet',
    },
    {
      field: 'status',
      headerName: 'Státusz',
      flex: 80,
      valueFormatter: (value: any) =>
        value === TenantStatus.Suspended
          ? 'Felfüggesztve'
          : value === TenantStatus.PendingDeletion
            ? 'Törlésre vár'
            : value === TenantStatus.Archived
              ? 'Archivált'
              : 'Aktív',
    },
    { field: 'contactEmail', headerName: 'Kapcsolat email', flex: 120 },
    {
      field: 'actions',
      type: 'actions',
      width: 56,
      getActions,
    },
  ];

  const draftColumns: any[] = [
    {
      field: 'tenantName',
      headerName: 'Bérlő név',
      flex: 120,
      valueGetter: (_: any, row: any) => {
        const name = row.payload?.tenant?.name?.trim();
        return name || '(névtelen draft)';
      },
    },
    {
      field: 'currentStep',
      headerName: 'Lépés',
      flex: 60,
      valueGetter: (_: any, row: any) => row.currentStep ?? 1,
    },
    {
      field: 'modifiedAt',
      headerName: 'Módosítva',
      flex: 120,
      valueGetter: (_: any, row: any) => {
        const raw = row.modifiedAt ?? row.ModifiedAt;
        if (!raw) return '—';
        const d = new Date(raw);
        return Number.isNaN(d.getTime()) ? raw : d.toLocaleString('hu-HU');
      },
    },
    {
      field: 'actions',
      type: 'actions',
      width: 100,
      getActions: (params: GridRowParams) => {
        const actions = [
          <GridActionsCellItem
            key="continue"
            icon={<ContinueIcon />}
            label="Folytatás"
            onClick={() => navigate(`/tenants/new/${params.row.id}`)}
          />,
        ];
        if (canPlatform(['ManageTenants'])) {
          actions.push(
            <GridActionsCellItem
              key="abandon"
              icon={<DeleteIcon color="error" />}
              label="Elvetés"
              onClick={() =>
                setParams({
                  open: true,
                  name: 'Biztosan elveted ezt a folyamatban lévő felvételt?',
                  onConfirm: () => handleAbandonDraft(params.row.id),
                })
              }
            />
          );
        }
        return actions;
      },
    },
  ];

  const handleDelete = (id: string) => {
    return tenantService.delete(id).then((response) => {
      if (response.hasError) {
        enqueueSnackbar((response.errorMessages ?? []).join(','), {
          variant: 'error',
        });
        return Promise.reject(response);
      }
      enqueueSnackbar('Sikeres törlés', { variant: 'success' });
      setRows((prev) => prev.filter((row) => row.id !== id));
    });
  };

  const handleAbandonDraft = (id: string) => {
    return tenantOnboardingService.abandon(id).then((response) => {
      if (response.hasError) {
        enqueueSnackbar((response.errorMessages ?? []).join(','), {
          variant: 'error',
        });
        // Resolve so the confirm dialog closes; error already shown.
        return;
      }
      enqueueSnackbar('Draft elvetve', { variant: 'success' });
      setDrafts((prev) => prev.filter((row) => row.id !== id));
    });
  };

  return (
    <Paper>
      <Grid container sx={{ p: 3 }}>
        <Grid size={12}>
          <h2>Bérlők</h2>
        </Grid>
        {canPlatform(['ManageTenants']) && (
          <Grid container size={12} sx={{ p: 2, justifyContent: 'end', gap: 1 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              component={RouterLink}
              to="/tenants/new"
            >
              Új bérlő
            </Button>
            <Tooltip title="Gyors létrehozás (rövid űrlap)">
              <IconButton component={RouterLink} to={`/tenants/create`} color="primary">
                <AddIcon />
              </IconButton>
            </Tooltip>
          </Grid>
        )}
        <Grid size={12}>
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              mb: 2,
              overflow: 'visible',
              '& .MuiTabs-scroller': { overflow: 'visible !important' },
            }}
          >
            <Tab label="Aktív bérlők" />
            <Tab
              label={
                <Badge
                  color="primary"
                  badgeContent={drafts.length || undefined}
                  invisible={drafts.length === 0}
                  sx={{ pr: 1.5 }}
                >
                  Folyamatban
                </Badge>
              }
              sx={{ overflow: 'visible' }}
            />
          </Tabs>
        </Grid>
        {tab === 0 && (
          <Grid size={12}>
            <DataList rows={rows} columns={columns} />
          </Grid>
        )}
        {tab === 1 && (
          <Grid size={12}>
            <DataList rows={drafts} columns={draftColumns} />
          </Grid>
        )}
        <ConfirmDeleteDialog />
      </Grid>
    </Paper>
  );
};

export default TenantsPage;
